import { z } from 'zod';

import type { AgentDockTab } from './AgentChatSessionProvider';

/** `localStorage` key of the persisted agent dock. Exported so the Organization switch flow can
 * clear it: the thread ids it holds are rows of the Organization that created them. */
export const AGENT_CHAT_SESSION_STORAGE_KEY = 'modernloop.agent-chat-session';
const STORAGE_VERSION = 1;

type PersistedAgentChatSession = {
  version: typeof STORAGE_VERSION;
  dockTabs: readonly AgentDockTab[];
  activeThreadId: string | null;
};

const persistedAgentChatSessionSchema = z.object({
  version: z.literal(STORAGE_VERSION),
  dockTabs: z.array(
    z.object({
      threadId: z.string().min(1),
      presentation: z.enum(['MINIMIZED', 'RESTORED']),
      hasUnread: z.boolean(),
    })
  ),
  activeThreadId: z.string().nullable(),
});

/** Parses the versioned browser storage boundary into a typed dock snapshot. */
export const parsePersistedAgentChatSession = (rawValue: string | null): PersistedAgentChatSession | null => {
  if (!rawValue) {
    return null;
  }
  try {
    const parsed = persistedAgentChatSessionSchema.safeParse(JSON.parse(rawValue));
    if (!parsed.success) {
      return null;
    }
    const dockTabs: AgentDockTab[] = [];
    const threadIds = new Set<string>();
    for (const dockTab of parsed.data.dockTabs) {
      if (threadIds.has(dockTab.threadId)) {
        return null;
      }
      threadIds.add(dockTab.threadId);
      dockTabs.push(dockTab);
    }
    return {
      version: STORAGE_VERSION,
      dockTabs,
      activeThreadId: parsed.data.activeThreadId,
    };
  } catch {
    return null;
  }
};

/** Reads a typed Agent dock snapshot without allowing malformed browser data into session state. */
export const loadPersistedAgentChatSession = (): PersistedAgentChatSession | null => {
  if (!globalThis.window) {
    return null;
  }
  return parsePersistedAgentChatSession(window.localStorage.getItem(AGENT_CHAT_SESSION_STORAGE_KEY));
};

/** Writes the current dock snapshot through the versioned browser storage boundary. */
export const savePersistedAgentChatSession = (
  dockTabs: readonly AgentDockTab[],
  activeThreadId: string | null
): void => {
  if (!globalThis.window) {
    return;
  }
  window.localStorage.setItem(
    AGENT_CHAT_SESSION_STORAGE_KEY,
    JSON.stringify({ version: STORAGE_VERSION, dockTabs, activeThreadId })
  );
};
